import { Component, computed, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationExtras, Router, RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AutomataService, Automation, RunningState } from '@vendasta/automata';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import { ASSISTANT_ID_REPUTATION_SPECIALIST, PlatformLocation } from '../../core/ai-assistant.constants';
import { AiConnectionsForm, ConnectionForm, ConnectionMetadata } from '../../core/forms';
import { ConnectionType } from '../../core/interfaces/ai-assistant.interface';
import { ACCOUNT_GROUP_ID_TOKEN, PLATFORM_LOCATION_TOKEN } from '../../core/tokens';
import { buildUrlWithQueryParams } from '../../core/utils/navigation.helper';

export type ReviewTriggerBanner = {
  titleKey: string;
  contentKey: string;
};

@Component({
  selector: 'ai-connections-form',
  imports: [
    AiAssistantI18nModule,
    MatCardModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    FormsModule,
    MatCheckboxModule,
    GalaxyLoadingSpinnerModule,
    GalaxyTooltipModule,
    MatIconModule,
    RouterModule,
    MatButtonModule,
    MatSlideToggleModule,
    GalaxyAlertModule,
  ],
  templateUrl: './ai-connections-form.component.html',
  styleUrl: './ai-connections-form.component.scss',
})
export class AiConnectionsFormComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly platformLocation = inject(PLATFORM_LOCATION_TOKEN);
  private readonly accountGroupId = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  private readonly analyticsService = inject(ProductAnalyticsService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly automataService = inject(AutomataService);

  connectionForms = input.required<AiConnectionsForm>();
  showManageButton = input<boolean>(true);
  reviewTriggerBanner = input<ReviewTriggerBanner | null>(null);
  blueprintAutomations = input<Automation[]>([]);

  blueprintAutomationRefresh = output<void>();

  protected readonly ConnectionType = ConnectionType;

  protected readonly showAllConnections = signal(false);
  private readonly CONNECTIONS_LIMIT = 4;

  protected readonly displayedConnections = computed(() => {
    const connections = this.connectionForms().controls;

    const sortedConnections = [...connections].sort((a, b) => {
      const aConnected = a.controls.connected.value ?? false;
      const bConnected = b.controls.connected.value ?? false;

      return Number(bConnected) - Number(aConnected);
    });

    if (this.showAllConnections() || sortedConnections.length <= this.CONNECTIONS_LIMIT) {
      return sortedConnections;
    }
    return sortedConnections.slice(0, this.CONNECTIONS_LIMIT);
  });

  protected readonly shouldShowToggleButton = computed(() => {
    return this.connectionForms().controls.length > this.CONNECTIONS_LIMIT;
  });

  protected readonly toggleButtonText = computed(() => {
    return this.showAllConnections()
      ? 'AI_ASSISTANT.SETTINGS.CHANNELS.VIEW_LESS'
      : 'AI_ASSISTANT.SETTINGS.CHANNELS.VIEW_MORE';
  });

  protected readonly shouldShowReviewTriggerBanner = computed(() => {
    return !!this.reviewTriggerBanner();
  });

  protected toggleShowAllConnections(): void {
    this.showAllConnections.set(!this.showAllConnections());
  }

  protected onConnectionToggle(connectionForm: ConnectionForm): void {
    if (this.router.url.includes(ASSISTANT_ID_REPUTATION_SPECIALIST)) {
      const isEnabled = connectionForm.controls.connected.value;
      this.analyticsService.trackEvent(
        'ai-assistant-reputation-specialist-connection-toggled',
        'reputation',
        'click',
        undefined,
        {
          assistantId: ASSISTANT_ID_REPUTATION_SPECIALIST,
          accountGroupId: this.accountGroupId(),
          connectionName: connectionForm.metadata.name,
          enabled: isEnabled,
        },
      );
    }
  }

  protected readonly RunningState = RunningState;

  protected readonly automationIdsWithCustomizableSteps = computed(() => {
    const automations = this.blueprintAutomations();
    return new Set(
      automations.filter((a) => (a.managementOptions?.customizableStepIds ?? []).length > 0).map((a) => a.id),
    );
  });

  protected onBlueprintAutomationToggle(event: MatSlideToggleChange, automation: Automation): void {
    const enabled = event.checked;

    const api$ = enabled
      ? this.automataService.activateOrUpdateAutomation(automation.id, '', undefined, false, true)
      : this.automataService.stopAutomationRunning(automation.id);

    const successKey = enabled
      ? 'AI_ASSISTANT.SETTINGS.TRIGGERS.TOGGLE_ON_SUCCESS'
      : 'AI_ASSISTANT.SETTINGS.TRIGGERS.TOGGLE_OFF_SUCCESS';

    firstValueFrom(api$)
      .then(() => this.snackbarService.openSuccessSnack(successKey))
      .catch(() => {
        event.source.checked = !enabled;
        this.snackbarService.openErrorSnack('AI_ASSISTANT.SETTINGS.TRIGGERS.TOGGLE_ERROR');
      })
      .finally(() => this.blueprintAutomationRefresh.emit());
  }

  protected readonly manageChannelsRoute = computed(() => {
    if (this.platformLocation === PlatformLocation.PLATFORM_LOCATION_BUSINESS_APP) {
      return `/account/location/${this.accountGroupId()}/administration/inbox`;
    } else {
      return '/inbox/settings';
    }
  });

  protected triggerAction(metadata: ConnectionMetadata): void {
    if (metadata.cta?.action) {
      if (this.router.url.includes(ASSISTANT_ID_REPUTATION_SPECIALIST)) {
        this.analyticsService.trackEvent(
          'ai-assistant-reputation-specialist-settings-clicked',
          'reputation',
          'click',
          undefined,
          {
            assistantId: ASSISTANT_ID_REPUTATION_SPECIALIST,
            accountGroupId: this.accountGroupId(),
            connectionName: metadata.name,
          },
        );
      }
      this.buildConnectionActionCallback(metadata)?.();
    }
  }

  protected buildConnectionActionCallback(metadata: ConnectionMetadata): (() => void) | undefined {
    if (!metadata?.cta?.action?.showButton) {
      return undefined;
    }

    const action = metadata.cta.action;

    if (action?.url) {
      if (action.newTab && !action.relativeRoute) {
        const finalUrl = buildUrlWithQueryParams(action.url, action.queryParams);
        return () => window.open(finalUrl, '_blank');
      }

      const navigationExtras: NavigationExtras = action.relativeRoute ? { relativeTo: this.route } : {};
      if (action.queryParams) {
        navigationExtras.queryParams = action.queryParams;
      }

      return () => this.router.navigate([action.url], navigationExtras);
    }

    if (action?.pathCommands) {
      return () => this.router.navigate(action.pathCommands);
    }

    if (action?.callback) {
      return action.callback;
    }
    return undefined;
  }
}
